import React from 'react';
import { Badge } from '@scuf/common';
import {
  IHierarchyBuilderNodeData,
  IOrganisationUsers
} from './onboardingModels';
import { TreeNode } from '../commonUtils/HierarchyTable';
import { IOption } from '@scuf/common/dist/components/Select/ISelectProps';

// Overriding the datatable's definition of celldata to make the class generic and hence maintain types
export interface ICellData<T = any, N = any> {
  value: N;
  rowData: T;
  name: string;
  field: string;
  rowIndex: number;
  editable: boolean;
  selected: boolean;
  sortable: boolean;
  placeholder?: string;
}

export const statusBadgeColor = (status: string) => {
  type colorType = 'red' | 'orange' | 'green' | 'blue' | 'grey';
  let color: colorType = 'grey';
  switch (status.toUpperCase()) {
    case 'EXPIRED':
    case 'CANCELED':
      color = 'red';
      break;
    case 'REGISTERED':
      color = 'green';
      break;
    case 'SENT':
      color = 'blue';
      break;
    default:
      break;
  }
  return color;
};

export const statusRenderer = (status: string) => {
  const color = statusBadgeColor(status);
  return <>{color !== 'grey' ? <Badge color={color} empty={true} /> : null}</>;
};

/**
 * Shouldn't use this for production, real data should come through with a readable string
 * @param propertyName Name of property to translate to readable string
 */
export const translatePropertyToReadable = (propertyName: string) => {
  const splitString = propertyName
    .split(/(?=[A-Z])/)
    .join(' ')
    .toLowerCase();
  const wholeString =
    propertyName.charAt(0).toUpperCase() +
    splitString.substr(1, splitString.length);
  return wholeString;
};

export const transformUserList = (users: IOrganisationUsers[]) => {
  users.forEach((aUser: IOrganisationUsers) => {
    aUser.displayName = aUser.displayName ? aUser.displayName : '-';
    aUser.phone = aUser.phone ? aUser.phone : '-';
    aUser.role =
      aUser.roles && aUser.roles.length > 0 ? aUser.roles[0].name : '-';
    aUser.status = aUser.registrationStatus.type;
  });
};

export const transformBulkUserList = (users: any[]) => {

  let transformedUsers: any = [];
  users.forEach((aUser: any) => {
    aUser.listOfFileProcess.forEach((element:any) => {
      let data: any = {
        action: element.action ? element.action : '-',
        email: element.email ? element.email : '-',
        fileProcessId: element.fileProcessId ? element.fileProcessId : '-',
        reason: element.reason ? element.reason : '-',
        status: element.status ? element.status : '-',
        role: element.role ? element.role : '-',
        displayName: element.displayName ? element.displayName : '-',
        id: element.id ? element.id : '-',
        site: element.site ? element.site : '-',
        fileName: aUser.fileName ? aUser.fileName : '-'
      };
      transformedUsers.push(data);
    });
  });
  return transformedUsers;
};

// Required because the API is sending JSON in the wrong format for js,
// so name and type attribute cant merge without this (Name !== name)
export const pascalToCamelCase = (stringToTransform: string) => {
  const strArr = stringToTransform.split('');
  strArr[0] = strArr[0].toLowerCase();
  return strArr.join('');
};

export const camelToPascalCase = (stringToTransform: string) => {
  const strArr = stringToTransform.split('');
  strArr[0] = strArr[0].toUpperCase();
  return strArr.join('');
};

// Restricting user to add email id having (+) sign like testProject1+89@gmail.com. Plus is truncated while sending in request and 
// user authorization fails in Supervisor
export const checkEmailIsValid = (email: string) => {
  let patternString = /^(([^<>()[\]\\.,;+:\s@"]+(\.[^<>()[\]\\.,;+:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const pattern = new RegExp(patternString);
  return email.match(pattern);
};


export const checkEmailIsWithRestrictedDomainValid = (email: string) => {  
    let restrictedDomain = [ "sy", "ir", "kp", "by", "cu", "ru", "ua" ];    
    const domain = email.substring(email.lastIndexOf('.') + 1);
    const val1 =  restrictedDomain.includes(domain);
    return !val1;  
};

export const checkSubcriptionAndOrderNumber = (value: string) => {
  if (value === null || value === undefined) {
    return false;
  }
  let patternString = /^([a-zA-Z0-9-]){1,13}$/;
  const pattern = new RegExp(patternString);
  return value.match(pattern);
};

export const propertiesToShowOnUI = [
  'PrimaryContactName',
  'PrimaryContactEmail',
  'Telephone',
  'Address',
  'TimeZone',
  'CommissioningStatus',
  'CountryCode',
  'Type',
  'ChildCount',
  'SheetVersion',
  'latitudeAndLongitude',
];
export type FieldElementType = 'input' | 'select';

export const editablePropertyInputTypes: {
  name: string;
  element: FieldElementType;
  type: string;
  patturn?: string;
  patturnError?: string;
}[] = [
  {
    name: 'name',
    element: 'input',
    type: 'text'
  },
  {
    name: 'primaryContactName',
    element: 'input',
    type: 'text'
  },
  {
    name: 'primaryContactEmail',
    element: 'input',
    type: 'email'
  },
  {
    name: 'telephone',
    element: 'input',
    type: 'tel',
    patturn: '^[0-9 ()+]{0,50}$',
    patturnError: 'Input must be formed of numbers and symbols only'
  },
  {
    name: 'address',
    element: 'input',
    type: 'text'
  },
  {
    name: 'timeZone',
    element: 'select',
    type: 'timeZones'
  },
  {
    name: 'type',
    element: 'select',
    type: 'nodeType'
  },
  {
    name: 'contactEmail',
    element: 'input',
    type: 'email'
  },
  {
    name: 'longitude',
    element: 'input',
    type: 'number'
  },
  {
    name: 'latitude',
    element: 'input',
    type: 'number'
  },
  {
    name: 'countryCode',
    element: 'input',
    type: 'text'
  }
];

export const sequenceOfField = [
  'primaryContactName',
  'primaryContactEmail',
  'childCount',
  'type',
  'commissioningStatus',
  'address',
  'telephone',
  'timeZone',
  'latitudeAndLongitude',
  'countryCode',
  'sheetVersion',
  'currency'
];

export const filterUIProperties = (
  data: Array<any>,
  propertySet?: Array<string>
) => {
  const fieldsToInclude = (propertySet
    ? propertySet
    : propertiesToShowOnUI
  ).map(pascalToCamelCase);


  return data.filter(datum => fieldsToInclude.includes(datum));
};

export const nodeTypes = [
  'Unspecified',
  'Business Unit',
  'Canton',
  'City',
  'Continent',
  'Country',
  'County',
  'Location',
  'Prefecture',
  'Province',
  'Region',
  'State',
  'Territory',
  'Site'
  // 'SMB Site'
];

export const nodeTypesOptions = nodeTypes.map(type => {
  return {
    text: type,
    value: type
  };
});

export const isNodeRootNode = (node: TreeNode<IHierarchyBuilderNodeData>) => {
  return node.data?.id === node.data?.rootNodeId;
};

// Handles adding the self id to the end, and handles if root node
export const getNodePath = (node: TreeNode<IHierarchyBuilderNodeData>) => {
  const joinedPath = node.data
    ? `${node.data.path?.length ? '/' : ''}${node.data.path.join('/')}`
    : '';
  return `${joinedPath}${!isNodeRootNode(node) ? '/' + node.data?.id : ''}`;
};

export const arrayToOptions = (arr: Array<string>): IOption[] => {
  return arr.map(value => ({
    text: value,
    value: value
  }));
};

export const timeZoneIds = [
  'Africa/Abidjan',
  'Africa/Accra',
  'Africa/Addis_Ababa',
  'Africa/Algiers',
  'Africa/Asmara',
  'Africa/Bamako',
  'Africa/Bangui',
  'Africa/Banjul',
  'Africa/Bissau',
  'Africa/Blantyre',
  'Africa/Brazzaville',
  'Africa/Bujumbura',
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Ceuta',
  'Africa/Conakry',
  'Africa/Dakar',
  'Africa/Dar_es_Salaam',
  'Africa/Djibouti',
  'Africa/Douala',
  'Africa/El_Aaiun',
  'Africa/Freetown',
  'Africa/Gaborone',
  'Africa/Harare',
  'Africa/Johannesburg',
  'Africa/Juba',
  'Africa/Kampala',
  'Africa/Khartoum',
  'Africa/Kigali',
  'Africa/Kinshasa',
  'Africa/Lagos',
  'Africa/Libreville',
  'Africa/Lome',
  'Africa/Luanda',
  'Africa/Lubumbashi',
  'Africa/Lusaka',
  'Africa/Malabo',
  'Africa/Maputo',
  'Africa/Maseru',
  'Africa/Mbabane',
  'Africa/Mogadishu',
  'Africa/Monrovia',
  'Africa/Nairobi',
  'Africa/Ndjamena',
  'Africa/Niamey',
  'Africa/Nouakchott',
  'Africa/Ouagadougou',
  'Africa/Porto-Novo',
  'Africa/Sao_Tome',
  'Africa/Tripoli',
  'Africa/Tunis',
  'Africa/Windhoek',
  'America/Adak',
  'America/Anchorage',
  'America/Anguilla',
  'America/Antigua',
  'America/Araguaina',
  'America/Argentina/Buenos_Aires',
  'America/Argentina/Catamarca',
  'America/Argentina/Cordoba',
  'America/Argentina/Jujuy',
  'America/Argentina/La_Rioja',
  'America/Argentina/Mendoza',
  'America/Argentina/Rio_Gallegos',
  'America/Argentina/Salta',
  'America/Argentina/San_Juan',
  'America/Argentina/San_Luis',
  'America/Argentina/Tucuman',
  'America/Argentina/Ushuaia',
  'America/Aruba',
  'America/Asuncion',
  'America/Atikokan',
  'America/Bahia',
  'America/Bahia_Banderas',
  'America/Barbados',
  'America/Belem',
  'America/Belize',
  'America/Blanc-Sablon',
  'America/Boa_Vista',
  'America/Bogota',
  'America/Boise',
  'America/Cambridge_Bay',
  'America/Campo_Grande',
  'America/Cancun',
  'America/Caracas',
  'America/Cayenne',
  'America/Cayman',
  'America/Chicago',
  'America/Chihuahua',
  'America/Costa_Rica',
  'America/Creston',
  'America/Cuiaba',
  'America/Curacao',
  'America/Danmarkshavn',
  'America/Dawson',
  'America/Dawson_Creek',
  'America/Denver',
  'America/Detroit',
  'America/Dominica',
  'America/Edmonton',
  'America/Eirunepe',
  'America/El_Salvador',
  'America/Fort_Nelson',
  'America/Fortaleza',
  'America/Glace_Bay',
  'America/Goose_Bay',
  'America/Grand_Turk',
  'America/Grenada',
  'America/Guadeloupe',
  'America/Guatemala',
  'America/Guayaquil',
  'America/Guyana',
  'America/Halifax',
  'America/Havana',
  'America/Hermosillo',
  'America/Indiana/Indianapolis',
  'America/Indiana/Knox',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Tell_City',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Inuvik',
  'America/Iqaluit',
  'America/Jamaica',
  'America/Juneau',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Kralendijk',
  'America/La_Paz',
  'America/Lima',
  'America/Los_Angeles',
  'America/Lower_Princes',
  'America/Maceio',
  'America/Managua',
  'America/Manaus',
  'America/Marigot',
  'America/Martinique',
  'America/Matamoros',
  'America/Mazatlan',
  'America/Menominee',
  'America/Merida',
  'America/Metlakatla',
  'America/Mexico_City',
  'America/Miquelon',
  'America/Moncton',
  'America/Monterrey',
  'America/Montevideo',
  'America/Montserrat',
  'America/Nassau',
  'America/New_York',
  'America/Nipigon',
  'America/Nome',
  'America/Noronha',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/Nuuk',
  'America/Ojinaga',
  'America/Panama',
  'America/Pangnirtung',
  'America/Paramaribo',
  'America/Phoenix',
  'America/Port-au-Prince',
  'America/Port_of_Spain',
  'America/Porto_Velho',
  'America/Puerto_Rico',
  'America/Punta_Arenas',
  'America/Rainy_River',
  'America/Rankin_Inlet',
  'America/Recife',
  'America/Regina',
  'America/Resolute',
  'America/Rio_Branco',
  'America/Santarem',
  'America/Santiago',
  'America/Santo_Domingo',
  'America/Sao_Paulo',
  'America/Scoresbysund',
  'America/Sitka',
  'America/St_Barthelemy',
  'America/St_Johns',
  'America/St_Kitts',
  'America/St_Lucia',
  'America/St_Thomas',
  'America/St_Vincent',
  'America/Swift_Current',
  'America/Tegucigalpa',
  'America/Thule',
  'America/Thunder_Bay',
  'America/Tijuana',
  'America/Toronto',
  'America/Tortola',
  'America/Vancouver',
  'America/Whitehorse',
  'America/Winnipeg',
  'America/Yakutat',
  'America/Yellowknife',
  'Antarctica/Casey',
  'Antarctica/Davis',
  'Antarctica/DumontDUrville',
  'Antarctica/Macquarie',
  'Antarctica/Mawson',
  'Antarctica/McMurdo',
  'Antarctica/Palmer',
  'Antarctica/Rothera',
  'Antarctica/Syowa',
  'Antarctica/Troll',
  'Antarctica/Vostok',
  'Arctic/Longyearbyen',
  'Asia/Aden',
  'Asia/Almaty',
  'Asia/Amman',
  'Asia/Anadyr',
  'Asia/Aqtau',
  'Asia/Aqtobe',
  'Asia/Ashgabat',
  'Asia/Atyrau',
  'Asia/Baghdad',
  'Asia/Bahrain',
  'Asia/Baku',
  'Asia/Bangkok',
  'Asia/Barnaul',
  'Asia/Beirut',
  'Asia/Bishkek',
  'Asia/Brunei',
  'Asia/Chita',
  'Asia/Choibalsan',
  'Asia/Colombo',
  'Asia/Damascus',
  'Asia/Dhaka',
  'Asia/Dili',
  'Asia/Dubai',
  'Asia/Dushanbe',
  'Asia/Famagusta',
  'Asia/Gaza',
  'Asia/Hebron',
  'Asia/Ho_Chi_Minh',
  'Asia/Hong_Kong',
  'Asia/Hovd',
  'Asia/Irkutsk',
  'Asia/Jakarta',
  'Asia/Jayapura',
  'Asia/Jerusalem',
  'Asia/Kabul',
  'Asia/Kamchatka',
  'Asia/Karachi',
  'Asia/Kathmandu',
  'Asia/Khandyga',
  'Asia/Kolkata',
  'Asia/Krasnoyarsk',
  'Asia/Kuala_Lumpur',
  'Asia/Kuching',
  'Asia/Kuwait',
  'Asia/Macau',
  'Asia/Magadan',
  'Asia/Makassar',
  'Asia/Manila',
  'Asia/Muscat',
  'Asia/Nicosia',
  'Asia/Novokuznetsk',
  'Asia/Novosibirsk',
  'Asia/Omsk',
  'Asia/Oral',
  'Asia/Phnom_Penh',
  'Asia/Pontianak',
  'Asia/Pyongyang',
  'Asia/Qatar',
  'Asia/Qostanay',
  'Asia/Qyzylorda',
  'Asia/Riyadh',
  'Asia/Sakhalin',
  'Asia/Samarkand',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Srednekolymsk',
  'Asia/Taipei',
  'Asia/Tashkent',
  'Asia/Tbilisi',
  'Asia/Tehran',
  'Asia/Thimphu',
  'Asia/Tokyo',
  'Asia/Tomsk',
  'Asia/Ulaanbaatar',
  'Asia/Urumqi',
  'Asia/Ust-Nera',
  'Asia/Vientiane',
  'Asia/Vladivostok',
  'Asia/Yakutsk',
  'Asia/Yangon',
  'Asia/Yekaterinburg',
  'Asia/Yerevan',
  'Atlantic/Azores',
  'Atlantic/Bermuda',
  'Atlantic/Canary',
  'Atlantic/Cape_Verde',
  'Atlantic/Faroe',
  'Atlantic/Madeira',
  'Atlantic/Reykjavik',
  'Atlantic/South_Georgia',
  'Atlantic/St_Helena',
  'Atlantic/Stanley',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Broken_Hill',
  'Australia/Currie',
  'Australia/Darwin',
  'Australia/Eucla',
  'Australia/Hobart',
  'Australia/Lindeman',
  'Australia/Lord_Howe',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Andorra',
  'Europe/Astrakhan',
  'Europe/Athens',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Bratislava',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Budapest',
  'Europe/Busingen',
  'Europe/Chisinau',
  'Europe/Copenhagen',
  'Europe/Dublin',
  'Europe/Gibraltar',
  'Europe/Guernsey',
  'Europe/Helsinki',
  'Europe/Isle_of_Man',
  'Europe/Istanbul',
  'Europe/Jersey',
  'Europe/Kaliningrad',
  'Europe/Kiev',
  'Europe/Kirov',
  'Europe/Lisbon',
  'Europe/Ljubljana',
  'Europe/London',
  'Europe/Luxembourg',
  'Europe/Madrid',
  'Europe/Malta',
  'Europe/Mariehamn',
  'Europe/Minsk',
  'Europe/Monaco',
  'Europe/Moscow',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Podgorica',
  'Europe/Prague',
  'Europe/Riga',
  'Europe/Rome',
  'Europe/Samara',
  'Europe/San_Marino',
  'Europe/Sarajevo',
  'Europe/Saratov',
  'Europe/Simferopol',
  'Europe/Skopje',
  'Europe/Sofia',
  'Europe/Stockholm',
  'Europe/Tallinn',
  'Europe/Tirane',
  'Europe/Ulyanovsk',
  'Europe/Uzhgorod',
  'Europe/Vaduz',
  'Europe/Vatican',
  'Europe/Vienna',
  'Europe/Vilnius',
  'Europe/Volgograd',
  'Europe/Warsaw',
  'Europe/Zagreb',
  'Europe/Zaporozhye',
  'Europe/Zurich',
  'Indian/Antananarivo',
  'Indian/Chagos',
  'Indian/Christmas',
  'Indian/Cocos',
  'Indian/Comoro',
  'Indian/Kerguelen',
  'Indian/Mahe',
  'Indian/Maldives',
  'Indian/Mauritius',
  'Indian/Mayotte',
  'Indian/Reunion',
  'Pacific/Apia',
  'Pacific/Auckland',
  'Pacific/Bougainville',
  'Pacific/Chatham',
  'Pacific/Chuuk',
  'Pacific/Easter',
  'Pacific/Efate',
  'Pacific/Enderbury',
  'Pacific/Fakaofo',
  'Pacific/Fiji',
  'Pacific/Funafuti',
  'Pacific/Galapagos',
  'Pacific/Gambier',
  'Pacific/Guadalcanal',
  'Pacific/Guam',
  'Pacific/Honolulu',
  'Pacific/Kiritimati',
  'Pacific/Kosrae',
  'Pacific/Kwajalein',
  'Pacific/Majuro',
  'Pacific/Marquesas',
  'Pacific/Midway',
  'Pacific/Nauru',
  'Pacific/Niue',
  'Pacific/Norfolk',
  'Pacific/Noumea',
  'Pacific/Pago_Pago',
  'Pacific/Palau',
  'Pacific/Pitcairn',
  'Pacific/Pohnpei',
  'Pacific/Port_Moresby',
  'Pacific/Rarotonga',
  'Pacific/Saipan',
  'Pacific/Tahiti',
  'Pacific/Tarawa',
  'Pacific/Tongatapu',
  'Pacific/Wake',
  'Pacific/Wallis'
];

export const COMMISSIONING_STATUS = 'CommissioningStatus';
export const GATEWAY_REGISTERED = 'GatewayRegistered';

export const sequenceFilterUIProperties = (data: Array<any>) => {
  return sequenceOfField.filter(fieldName => data.includes(fieldName));
};

export const findMetaDataValue = (key: string, metadata: any[]) => {
  let filteredMetaData = [];
  if (key && metadata) {
    filteredMetaData = metadata.filter(
      (data: { name: string; value: any }) => data.name === key
    );
  }  
  if (filteredMetaData.length === 1) {
    return filteredMetaData[0].value;
  } else return null;
};